import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faSpinner, faListCheck, faLink } from '@fortawesome/free-solid-svg-icons';
import { orderApi } from '../api/orderApi';
import { useRisk } from '../contexts/RiskContext';

const emptyRow = (symbol = '') => ({
  symbol: String(symbol || '').toUpperCase(),
  side: 'buy',
  orderBy: 'qty',
  qty: '',
  notional: '',
  type: 'market',
  timeInForce: 'day',
  limitPrice: '',
  stopPrice: '',
  extendedHours: false,
});

const MultiStockOrderForm = ({ symbol: seedSymbol = '', onOrderSubmitted }) => {
  const { checkOrder } = useRisk();
  const [rows, setRows] = useState([
    emptyRow(seedSymbol),
    emptyRow(''),
  ]);
  const [mode, setMode] = useState('parallel'); // parallel | chain
  const [trigger, setTrigger] = useState('fill'); // fill | accepted
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);

  const updateRow = (index, patch) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const filledCount = useMemo(
    () =>
      rows.filter((r) => {
        const hasSize =
          (r.orderBy === 'qty' && r.qty !== '' && Number(r.qty) > 0) ||
          (r.orderBy === 'notional' && r.notional !== '' && Number(r.notional) > 0);
        return r.symbol && hasSize;
      }).length,
    [rows]
  );

  const buildOrderPayload = (row) => {
    const payload = {
      symbol: String(row.symbol).trim().toUpperCase(),
      side: row.side,
      type: row.type,
      timeInForce: row.timeInForce,
      extendedHours: !!row.extendedHours,
    };
    if (row.orderBy === 'notional') {
      payload.notional = parseFloat(row.notional);
    } else {
      payload.qty = parseFloat(row.qty);
    }
    if (row.type === 'limit' || row.type === 'stop_limit') {
      payload.limitPrice = parseFloat(row.limitPrice);
    }
    if (row.type === 'stop' || row.type === 'stop_limit') {
      payload.stopPrice = parseFloat(row.stopPrice);
    }
    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResults(null);

    const ready = rows.filter((r) => {
      const hasSize =
        (r.orderBy === 'qty' && r.qty !== '' && Number(r.qty) > 0) ||
        (r.orderBy === 'notional' && r.notional !== '' && Number(r.notional) > 0);
      return r.symbol && hasSize;
    });

    if (mode === 'chain' && ready.length < 2) {
      setError('Chain needs at least 2 ready orders (row 1 triggers row 2, …)');
      return;
    }
    if (ready.length < 1) {
      setError('Add at least one row with symbol and qty/notional');
      return;
    }

    for (const row of ready) {
      if ((row.type === 'limit' || row.type === 'stop_limit') && !(Number(row.limitPrice) > 0)) {
        setError(`${row.symbol}: limit price required`);
        return;
      }
      if ((row.type === 'stop' || row.type === 'stop_limit') && !(Number(row.stopPrice) > 0)) {
        setError(`${row.symbol}: stop price required`);
        return;
      }
    }

    const orders = ready.map(buildOrderPayload);

    try {
      for (const order of orders) {
        // eslint-disable-next-line no-await-in-loop
        const risk = await checkOrder(order);
        if (risk?.blocks?.length) {
          setError(`${order.symbol}: ${risk.blocks[0]}`);
          return;
        }
      }
    } catch {
      /* risk optional — continue */
    }

    try {
      setSubmitting(true);
      const response =
        mode === 'chain'
          ? await orderApi.submitChainedOrders({ orders, trigger, timeoutMs: 10 * 60 * 1000 })
          : await orderApi.submitBatchOrders({ orders });
      setResults(response.data || response);
      const okOnes = (response.data?.results || []).filter((r) => r.ok).map((r) => r.data);
      if (okOnes.length) onOrderSubmitted?.(okOnes[0]);
      if (response.data?.failed === 0 && (mode !== 'chain' || response.data?.completed)) {
        setRows([emptyRow(seedSymbol), emptyRow('')]);
      }
    } catch (err) {
      console.error(mode === 'chain' ? 'Chain order error:' : 'Batch order error:', err);
      setError(err.response?.data?.error || err.message || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  const isChain = mode === 'chain';

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-md border border-hairline overflow-hidden text-[11px]">
          <button
            type="button"
            onClick={() => setMode('parallel')}
            className={`h-7 px-2.5 ${!isChain ? 'bg-[var(--lt-action)] text-white' : 'bg-canvas text-ink hover:bg-elevated'}`}
          >
            Independent
          </button>
          <button
            type="button"
            onClick={() => setMode('chain')}
            className={`h-7 px-2.5 inline-flex items-center gap-1 border-l border-hairline ${isChain ? 'bg-[var(--lt-action)] text-white' : 'bg-canvas text-ink hover:bg-elevated'}`}
          >
            <FontAwesomeIcon icon={faLink} className="text-[9px]" />
            Chain
          </button>
        </div>
        {isChain ? (
          <select
            value={trigger}
            onChange={(e) => setTrigger(e.target.value)}
            className="h-7 rounded border border-hairline bg-canvas px-1.5 text-[11px]"
            title="When to fire the next order"
          >
            <option value="fill">Next on fill</option>
            <option value="accepted">Next on accept</option>
          </select>
        ) : null}
        <p className="text-[11px] text-muted">
          {isChain
            ? `Row 1 → wait ${trigger === 'accepted' ? 'accept' : 'fill'} → row 2 → … (${filledCount} ready)`
            : `Basket — all at once, up to 25 (${filledCount} ready)`}
        </p>
        <button
          type="button"
          onClick={() => setRows((prev) => [...prev, emptyRow('')])}
          disabled={rows.length >= (isChain ? 10 : 25)}
          className="h-7 px-2 rounded-md border border-hairline text-[11px] text-[var(--lt-action)] hover:bg-elevated disabled:opacity-40 inline-flex items-center gap-1"
        >
          <FontAwesomeIcon icon={faPlus} className="text-[9px]" />
          Add row
        </button>
        <button
          type="submit"
          disabled={submitting || filledCount < (isChain ? 2 : 1)}
          className="ml-auto h-8 px-3 rounded-md bg-[var(--lt-action)] text-white text-[11px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          {submitting ? (
            <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
          ) : isChain ? (
            <FontAwesomeIcon icon={faLink} />
          ) : (
            <FontAwesomeIcon icon={faListCheck} />
          )}
          {submitting
            ? isChain
              ? 'Waiting for fills…'
              : 'Submitting…'
            : isChain
              ? `Chain ${filledCount || ''} orders`
              : `Submit ${filledCount || ''} order${filledCount === 1 ? '' : 's'}`}
        </button>
      </div>

      {error ? <div className="text-[11px] text-[var(--lt-bear)]">{error}</div> : null}
      {results ? (
        <div className="text-[11px] space-y-1">
          <div
            className={
              results.failed || (results.mode === 'chain' && !results.completed)
                ? 'text-[var(--lt-warning)]'
                : 'text-[var(--lt-bull)]'
            }
          >
            {results.mode === 'chain' ? 'Chain · ' : ''}
            {results.submitted}/{results.total} submitted
            {results.failed ? ` · stopped/failed` : ''}
            {results.completed ? ' · complete' : ''}
          </div>
          <div className="max-h-28 overflow-y-auto rounded border border-hairline divide-y divide-hairline bg-canvas">
            {(results.results || []).map((r) => (
              <div key={`${r.index}-${r.symbol}`} className="px-2 py-1 flex gap-2 flex-wrap">
                <span className="font-medium text-ink w-14">{r.symbol || '—'}</span>
                {results.mode === 'chain' && r.index > 0 ? (
                  <span className="text-muted text-[10px]">← triggered</span>
                ) : null}
                {r.ok ? (
                  <span className="text-[var(--lt-bull)]">
                    {r.phase ? `${r.phase} · ` : ''}
                    {r.data?.status || 'ok'} · {r.data?.id}
                  </span>
                ) : (
                  <span className="text-[var(--lt-bear)]">{r.error}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="rounded-md border border-hairline overflow-hidden">
        <div className="overflow-x-auto max-h-[360px] overflow-y-auto">
          <table className="w-full text-[11px]">
            <thead className="sticky top-0 bg-surface border-b border-hairline">
              <tr className="text-left text-[10px] uppercase tracking-wide text-muted">
                {isChain ? <th className="px-2 py-1.5 w-8">#</th> : null}
                <th className="px-2 py-1.5">Symbol</th>
                <th className="px-2 py-1.5">Side</th>
                <th className="px-2 py-1.5">Size</th>
                <th className="px-2 py-1.5">Type</th>
                <th className="px-2 py-1.5">Limit</th>
                <th className="px-2 py-1.5">Stop</th>
                <th className="px-2 py-1.5">TIF</th>
                <th className="px-2 py-1.5">Ext</th>
                <th className="px-2 py-1.5" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={`row-${index}`} className="border-t border-hairline">
                  {isChain ? (
                    <td className="px-2 py-1 text-muted font-medium">{index + 1}</td>
                  ) : null}
                  <td className="px-2 py-1">
                    <input
                      value={row.symbol}
                      onChange={(e) => updateRow(index, { symbol: e.target.value.toUpperCase() })}
                      placeholder="AAPL"
                      className="h-7 w-20 rounded border border-hairline bg-canvas px-1.5 font-medium uppercase"
                    />
                  </td>
                  <td className="px-2 py-1">
                    <select
                      value={row.side}
                      onChange={(e) => updateRow(index, { side: e.target.value })}
                      className="h-7 rounded border border-hairline bg-canvas px-1"
                    >
                      <option value="buy">Buy</option>
                      <option value="sell">Sell</option>
                    </select>
                  </td>
                  <td className="px-2 py-1">
                    <div className="flex items-center gap-1">
                      <select
                        value={row.orderBy}
                        onChange={(e) => updateRow(index, { orderBy: e.target.value })}
                        className="h-7 rounded border border-hairline bg-canvas px-1 text-[10px]"
                      >
                        <option value="qty">Qty</option>
                        <option value="notional">$</option>
                      </select>
                      {row.orderBy === 'qty' ? (
                        <input
                          type="number"
                          min="0"
                          step="any"
                          value={row.qty}
                          onChange={(e) => updateRow(index, { qty: e.target.value })}
                          className="h-7 w-16 rounded border border-hairline bg-canvas px-1"
                        />
                      ) : (
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={row.notional}
                          onChange={(e) => updateRow(index, { notional: e.target.value })}
                          className="h-7 w-20 rounded border border-hairline bg-canvas px-1"
                        />
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-1">
                    <select
                      value={row.type}
                      onChange={(e) => updateRow(index, { type: e.target.value })}
                      className="h-7 rounded border border-hairline bg-canvas px-1"
                    >
                      <option value="market">Market</option>
                      <option value="limit">Limit</option>
                      <option value="stop">Stop</option>
                      <option value="stop_limit">Stop limit</option>
                    </select>
                  </td>
                  <td className="px-2 py-1">
                    <input
                      type="number"
                      step="0.01"
                      disabled={row.type !== 'limit' && row.type !== 'stop_limit'}
                      value={row.limitPrice}
                      onChange={(e) => updateRow(index, { limitPrice: e.target.value })}
                      className="h-7 w-20 rounded border border-hairline bg-canvas px-1 disabled:opacity-40"
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      type="number"
                      step="0.01"
                      disabled={row.type !== 'stop' && row.type !== 'stop_limit'}
                      value={row.stopPrice}
                      onChange={(e) => updateRow(index, { stopPrice: e.target.value })}
                      className="h-7 w-20 rounded border border-hairline bg-canvas px-1 disabled:opacity-40"
                    />
                  </td>
                  <td className="px-2 py-1">
                    <select
                      value={row.timeInForce}
                      onChange={(e) => updateRow(index, { timeInForce: e.target.value })}
                      className="h-7 rounded border border-hairline bg-canvas px-1"
                    >
                      <option value="day">DAY</option>
                      <option value="gtc">GTC</option>
                      <option value="ioc">IOC</option>
                      <option value="fok">FOK</option>
                    </select>
                  </td>
                  <td className="px-2 py-1 text-center">
                    <input
                      type="checkbox"
                      checked={row.extendedHours}
                      onChange={(e) => updateRow(index, { extendedHours: e.target.checked })}
                    />
                  </td>
                  <td className="px-2 py-1">
                    <button
                      type="button"
                      disabled={rows.length <= 1}
                      onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
                      className="h-7 w-7 rounded text-[var(--lt-bear)] hover:bg-elevated disabled:opacity-30"
                      title="Remove"
                    >
                      <FontAwesomeIcon icon={faTrash} className="text-[10px]" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </form>
  );
};

export default MultiStockOrderForm;
